import 'leaflet/dist/leaflet.css';
import { AimOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { MapContainer, Marker, ZoomControl } from 'react-leaflet';
import type { AdminLocation } from '@goelectric/shared-types';
import { pinIcon } from '../utils/mapPin';
import { OsmTileLayer } from './OsmTileLayer';
import { LocationStationsPanel } from './LocationStationsPanel';

/** Zoom de detalle: una sola ubicación, queremos ver su entorno inmediato. */
const DETAIL_MAP_ZOOM = 16;

/**
 * Mapa de la ubicación en el detalle (reemplaza al link a OpenStreetMap).
 *
 * **Solo LECTURA del punto**: el pin refleja `location.coordinates` y nada del mapa lo
 * escribe. Por eso el `Marker` NO es `draggable` y el mapa no tiene ningún handler que
 * persista lat/lng: arrastrar y hacer zoom mueven SOLO la cámara, nunca las coordenadas.
 * (Distinto del picker del wizard, que sí es bidireccional con el form.)
 *
 * Desde TG-125 la vista SÍ es interactiva (drag + zoom) para poder ubicarse: se habilitan
 * `dragging`, `scrollWheelZoom`, `doubleClickZoom`, `touchZoom` y `keyboard` — todas
 * interacciones de VISTA. La rueda se habilita siempre, igual que el resto de los mapas de
 * la app (`LocationMapPicker` y el `StationMap` del NOC usan `scrollWheelZoom`), para no
 * inventar un criterio distinto; tradeoff conocido: sobre el mapa la rueda no scrollea la
 * página. Si molesta, la alternativa es habilitarla solo con el mapa enfocado (click).
 * Los controles +/− viven abajo-izquierda: el `ZoomControl` comparte la esquina que
 * `global.css` ya mueve a la izquierda (junto a la atribución de OSM).
 *
 * Reusa el pin (`pinIcon`) y los tiles (`OsmTileLayer`) del picker para que ambos mapas se
 * vean idénticos. El panel de cargadores (`LocationStationsPanel`) emerge anclado al mapa;
 * solo se monta cuando `showStations` es `true` (gate por rol, ver `LocationDetailPage`).
 *
 * ponytail: posibilidad futura mencionada por el Tech Lead —mostrar OTROS puntos sobre el
 * mapa (otras ubicaciones/cargadores)—. NO se implementa acá: hoy el mapa es de una sola
 * ubicación. Si se retoma, evaluar tiles/markers extra y el filtro por cercanía.
 */
export function LocationDetailMap({
  location,
  showStations,
}: {
  location: AdminLocation;
  showStations: boolean;
}) {
  const { coordinates } = location;
  if (!coordinates) {
    return null;
  }

  const center: [number, number] = [coordinates.latitude, coordinates.longitude];
  const osmHref = `https://www.openstreetmap.org/?mlat=${coordinates.latitude}&mlon=${coordinates.longitude}`;

  return (
    <div className="locaciones-map" role="region" aria-label="Mapa de la ubicación">
      <MapContainer
        className="locaciones-map__canvas locaciones-map__canvas--readonly"
        center={center}
        zoom={DETAIL_MAP_ZOOM}
        dragging
        scrollWheelZoom
        doubleClickZoom
        touchZoom
        keyboard
        boxZoom={false}
        zoomControl={false}
        style={{ height: 360, borderRadius: 8 }}
      >
        <OsmTileLayer />
        {/* Controles +/− en la esquina que el CSS manda a abajo-izquierda. */}
        <ZoomControl position="bottomright" />
        {/* Pin de solo lectura: sin `draggable`, la posición sale de `location.coordinates`. */}
        <Marker position={center} icon={pinIcon()} />
      </MapContainer>

      {/* Info (ciudad/departamento + coordenadas) arriba-izquierda para no chocar con el
          panel de cargadores (derecha) ni con la atribución de OSM (abajo-izquierda). */}
      <div className="locaciones-map__info locaciones-map__info--detail">
        <div className="locaciones-map__info-row">
          <EnvironmentOutlined aria-hidden />
          <span>
            {location.city && <strong>{location.city}</strong>}
            {location.city && location.state && ' · '}
            <span className="locaciones-map__info-muted">{location.state}</span>
          </span>
        </div>
        <div className="locaciones-map__info-row locaciones-map__info-row--coords">
          <AimOutlined aria-hidden />
          <span className="locaciones-map__info-coords">
            <span className="locaciones-map__info-muted">Lat</span>{' '}
            <span>{coordinates.latitude.toFixed(5)}</span>
            {' · '}
            <span className="locaciones-map__info-muted">Lng</span>{' '}
            <span>{coordinates.longitude.toFixed(5)}</span>
          </span>
        </div>
        <div className="locaciones-map__info-row">
          <a href={osmHref} target="_blank" rel="noopener noreferrer">
            Ver en OpenStreetMap
          </a>
        </div>
      </div>

      {showStations && <LocationStationsPanel locationId={location.id} />}
    </div>
  );
}
